/**
 * Pump Recommendation Engine
 * Scores pump models against DAF requirements for optimal selection.
 */

export interface ScoredPump {
  id: string;
  code: string;
  manufacturerName: string;
  seriesName: string;
  motorKw: number | null;
  motorHp: number | null;
  maxFlowM3h: number | null;
  maxPressureBar: number | null;
  score: number;
  verdict: 'ideal' | 'good' | 'acceptable' | 'undersized' | 'oversized' | 'unknown';
  verdictLabel: string;
  flowRatio: number; // requiredFlow / maxFlow
  pressureRatio: number; // requiredPressure / maxPressure
  notes: string;
}

export interface RawPumpModel {
  id: string;
  code: string;
  manufacturerName: string;
  seriesName: string;
  motorKw: number | null;
  motorHp: number | null;
  maxFlowM3h: number | null;
  maxPressureBar: number | null;
}

/**
 * Score a single pump against DAF requirements.
 * Returns 0–100 score plus detailed verdict.
 *
 * Scoring logic:
 * - Flow sweet spot: 40–80% of max flow = best efficiency zone
 * - Pressure: must exceed required by at least 5%
 * - Heavy penalty for < 30% flow (oversized) or > 95% flow (undersized)
 * - Disqualify if flow > 100% max or pressure < required
 */
export function scorePump(
  model: RawPumpModel,
  requiredFlow: number,
  requiredPressure: number
): ScoredPump {
  const maxFlow = model.maxFlowM3h;
  const maxPressure = model.maxPressureBar;

  // Unknown specs — still show but mark as unknown
  if (!maxFlow || !maxPressure || maxFlow <= 0 || maxPressure <= 0) {
    return {
      ...model,
      score: 0,
      verdict: 'unknown',
      verdictLabel: 'Check specs',
      flowRatio: 0,
      pressureRatio: 0,
      notes: 'Missing flow or pressure specifications',
    };
  }

  const flowRatio = requiredFlow / maxFlow;
  const pressureRatio = requiredPressure / maxPressure;

  // Disqualify: undersized
  if (flowRatio > 1.0 || pressureRatio > 1.0) {
    const reason = flowRatio > 1.0
      ? `Required flow ${requiredFlow.toFixed(1)} m³/h exceeds max ${maxFlow} m³/h`
      : `Required pressure ${requiredPressure} bar exceeds max ${maxPressure} bar`;
    return {
      ...model,
      score: 0,
      verdict: 'undersized',
      verdictLabel: 'Too small',
      flowRatio,
      pressureRatio,
      notes: reason,
    };
  }

  // Flow score: parabola peaking at 0.6 (60% of max)
  // Score = 100 - |flowRatio - 0.6| * 250  (so 0.4→80, 0.6→100, 0.8→80, 0.3→25)
  let flowScore = 100 - Math.abs(flowRatio - 0.6) * 250;
  flowScore = Math.max(0, Math.min(100, flowScore));

  // Pressure score: linear from 0.5→50 to 1.0→100, cap at 1.3→100
  let pressureScore = pressureRatio >= 1.0 ? 100 : pressureRatio * 100;
  pressureScore = Math.min(100, pressureScore);

  // Oversize penalty: if flowRatio < 0.35, apply penalty
  if (flowRatio < 0.35) {
    flowScore *= 0.3; // heavy penalty
  }

  // Weighted average: flow matters more for DAF (60/40)
  const rawScore = flowScore * 0.6 + pressureScore * 0.4;

  // Determine verdict
  let verdict: ScoredPump['verdict'];
  let verdictLabel: string;
  let notes: string;

  if (flowRatio < 0.3) {
    verdict = 'oversized';
    verdictLabel = 'Too big';
    notes = `Operating at only ${(flowRatio * 100).toFixed(0)}% of max flow — inefficient, consider a smaller pump`;
  } else if (flowRatio < 0.45) {
    verdict = 'acceptable';
    verdictLabel = 'Acceptable';
    notes = `Operating at ${(flowRatio * 100).toFixed(0)}% of max flow — usable but not ideal`;
  } else if (flowRatio <= 0.85) {
    verdict = 'ideal';
    verdictLabel = 'Ideal match';
    notes = `Operating at ${(flowRatio * 100).toFixed(0)}% of max flow — sweet spot for efficiency`;
  } else {
    verdict = 'good';
    verdictLabel = 'Good match';
    notes = `Operating at ${(flowRatio * 100).toFixed(0)}% of max flow — close to limit, verify NPSH`;
  }

  return {
    ...model,
    score: Math.round(rawScore),
    verdict,
    verdictLabel,
    flowRatio,
    pressureRatio,
    notes,
  };
}

/**
 * Rank all pumps and return top N recommendations.
 */
export function getRecommendedPumps(
  models: RawPumpModel[],
  requiredFlow: number,
  requiredPressure: number,
  limit = 5
): ScoredPump[] {
  if (!requiredFlow || !requiredPressure) return [];

  const scored = models.map(m => scorePump(m, requiredFlow, requiredPressure));

  // Sort: highest score first, but put ideal/good before acceptable/oversized even if scores overlap
  const verdictOrder = { ideal: 0, good: 1, acceptable: 2, oversized: 3, unknown: 4, undersized: 5 };
  scored.sort((a, b) => {
    if (a.score !== b.score) return b.score - a.score;
    return verdictOrder[a.verdict] - verdictOrder[b.verdict];
  });

  return scored.slice(0, limit);
}

/**
 * Get the single best pump recommendation.
 */
export function getBestPump(
  models: RawPumpModel[],
  requiredFlow: number,
  requiredPressure: number
): ScoredPump | null {
  const recs = getRecommendedPumps(models, requiredFlow, requiredPressure, 1);
  return recs[0] ?? null;
}

/** Color classes for verdict badges */
export const verdictColors: Record<ScoredPump['verdict'], { bg: string; text: string; border: string; dot: string }> = {
  ideal:     { bg: 'bg-emerald-500/10', text: 'text-emerald-400', border: 'border-emerald-500/20', dot: 'bg-emerald-400' },
  good:      { bg: 'bg-sky-500/10',     text: 'text-sky-400',     border: 'border-sky-500/20',     dot: 'bg-sky-400' },
  acceptable:{ bg: 'bg-amber-500/10',   text: 'text-amber-400',   border: 'border-amber-500/20',   dot: 'bg-amber-400' },
  oversized: { bg: 'bg-orange-500/10',  text: 'text-orange-400',  border: 'border-orange-500/20',  dot: 'bg-orange-400' },
  undersized:{ bg: 'bg-rose-500/10',    text: 'text-rose-400',    border: 'border-rose-500/20',    dot: 'bg-rose-400' },
  unknown:   { bg: 'bg-slate-500/10',   text: 'text-slate-400',   border: 'border-slate-500/20',   dot: 'bg-slate-400' },
};
